import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { getUserFriendlyError } from "@/lib/errors";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/admin/products");
      }
    });
  }, [navigate]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/admin/products`,
        },
      });
      if (error) throw error;
      toast({ 
        title: "Magic link verzonden!", 
        description: "Check je e-mail voor de inloglink." 
      });
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Inloggen"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Inloggen | Bo Barbers"
        description="Log in op het Bo Barbers admin paneel met een veilige magic link via e-mail."
        path="/auth"
      />
      <Header />
      <h1 className="sr-only">Inloggen bij Bo Barbers</h1>
      <div className="flex-1 flex items-center justify-center py-32 px-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              Inloggen met Magic Link
            </CardTitle>
            <CardDescription>
              Ontvang een veilige inloglink via e-mail. Geen wachtwoord nodig.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="jouw@email.nl"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Bezig..." : "Verstuur Magic Link"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;
